/**
 * @fileoverview Story Planning schemas for storylines with beats and story questions.
 *
 * This module provides the data model for tracking:
 * - Storylines: A/B/C stories composed of beats (external events at structural phases)
 * - Story Questions: Dramatic hooks nested within their parent storylines
 * - Chapter-Storyline mapping: Which storylines are active in which chapters, with tension levels
 *
 * Storylines focus on the EXTERNAL journey (plot events) with optional internal/thematic state.
 * Character arcs (separate schema) focus on the INTERNAL journey.
 */

import { z } from 'zod';
import { StructurePhaseSchema, STRUCTURE_PHASE_GUIDANCE } from './structure-phases';

// =============================================================================
// TENSION LEVEL SCHEMA
// =============================================================================

/**
 * Tension levels for tracking narrative arc within a storyline.
 * - low: Breathing room, setup, aftermath
 * - rising: Building pressure, escalating stakes
 * - high: Peak tension, crisis points, climax moments
 * - release: Catharsis, resolution, temporary relief
 */
export const TensionLevelEnum = z.enum(['low', 'rising', 'high', 'release']);

// =============================================================================
// STORYLINE PRIORITY
// =============================================================================

/**
 * Priority levels for storylines following the A/B/C story pattern.
 * - A: The External Spine — primary external plot defining the overarching objective, main conflict, and physical stakes
 * - B: The Internal/Thematic Spine — main emotional, relational, or thematic arc(s) running parallel to the A-Story
 * - C: The Concurrent Subplot — secondary storylines, antagonist maneuvers, or background events unfolding parallel to the main timeline
 */
export const StorylinePriorityEnum = z.enum(['A', 'B', 'C']);

// =============================================================================
// STORY QUESTION SCHEMA
// =============================================================================

/**
 * Status for story questions.
 * - open: The question has been posed but not yet resolved
 * - answered: The question has been resolved in the narrative
 */
export const StoryQuestionStatusEnum = z.enum(['open', 'answered']);

/**
 * A story question is a dramatic hook that keeps readers engaged.
 * Questions are raised early and answered later, creating narrative tension.
 * Each question belongs to a specific storyline.
 */
export const StoryQuestionSchema = z.object({
  id: z.string().describe('Unique identifier for this question'),
  question: z.string().describe('The dramatic question (e.g., "Will Marion forgive Indy?")'),
  status: StoryQuestionStatusEnum.default('open').describe('Whether this question is still open or has been answered'),
});

// =============================================================================
// STORYLINE BEAT SCHEMA
// =============================================================================

/**
 * Helper to build semantic guidance for storyline beats.
 * Extracts storyline-specific guidance from the shared structure phase config.
 */
function buildStorylineBeatGuidance(): string {
  const phases = Object.entries(STRUCTURE_PHASE_GUIDANCE)
    .map(([phase, config]) => `• ${phase}: ${config.storylineGuidance}`)
    .join('\n');
  return `Each beat represents a plot event at a key structural moment.\n\nPhase guidance:\n${phases}`;
}

/**
 * A single beat in a storyline, representing an external plot event.
 *
 * Storyline beats focus on the EXTERNAL journey (80%) with optional thematic state (20%).
 *
 * IMPORTANT: Beats have TWO phase fields:
 * - globalPhase: Where in the OVERALL story timeline this beat occurs (determines matrix COLUMN)
 * - storylinePhase: What structural function this beat serves for THIS storyline (determines COLOR)
 *
 * This allows storylines to start, climax, and end at different points in the global narrative.
 * For example, a C-storyline might have its Inciting Incident (storylinePhase) occur during
 * the global Midpoint (globalPhase), shown as a yellow beat in the Midpoint column.
 */
export const StorylineBeatSchema = z.object({
  id: z.string().uuid().describe('Unique identifier for this beat.'),

  globalPhase: StructurePhaseSchema.describe(
    'Where in the GLOBAL story timeline this beat occurs. ' +
    'Determines which COLUMN the beat appears in on the matrix. ' +
    'This represents the reader/audience experience of when this event happens in the overall narrative.'
  ),

  storylinePhase: StructurePhaseSchema.describe(
    'What structural function this beat serves for THIS STORYLINE\'s internal arc. ' +
    'Determines the COLOR of the beat on the matrix. ' +
    'A storyline\'s Inciting Incident might occur at the global Midpoint if that storyline starts late. ' +
    'A storyline\'s Climax might occur at the global Break Into Three if it resolves early.'
  ),

  plotEvent: z.string().describe(
    'The concrete, observable event that occurs in the story world. ' +
    'Focus on what physically happens—actions, discoveries, confrontations, arrivals, departures. ' +
    'Examples: "The rebel base is destroyed", "Hero discovers the hidden map", "The villain escapes prison", ' +
    '"The kingdom is at peace" (Initial State), "The new king is crowned" (Final State).'
  ),

  narrativeState: z.string().optional().describe(
    'The emotional or thematic state of this storyline thread at this moment (optional). ' +
    'How does this event affect the narrative stakes or emotional tenor? ' +
    'Examples: "Hope is lost", "The mystery deepens", "Trust is shattered", "Tension reaches breaking point".'
  ),

  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward the storyline\'s central objective at this moment. ' +
    '+1 = Major advancement toward the goal (victory, breakthrough, key discovery). ' +
    '-1 = Significant setback or reversal (defeat, loss, major obstacle). ' +
    '0 = Neutral, lateral movement, or setup without clear progress. ' +
    'A-storylines typically show setbacks at Midpoint and Break Into Three, with victory at Climax.'
  ),

  order: z.number().int().min(0).default(0).describe(
    'Sequence order of this beat within its globalPhase cell. ' +
    'When multiple beats exist in the same globalPhase, order determines their sequence. ' +
    '0 = first beat, 1 = second beat, etc. Used for narrative ordering within a phase. ' +
    'Maximum beats per globalPhase depends on priority: A=6, B=4, C=2.'
  ),
}).describe(
  'A beat in a storyline representing an external plot event. ' +
  buildStorylineBeatGuidance()
);

export type StorylineBeat = z.infer<typeof StorylineBeatSchema>;

// =============================================================================
// STORYLINE SCHEMA
// =============================================================================

/**
 * A storyline represents a distinct narrative thread that runs through the story.
 * Storylines are composed of beats—external plot events at structural phases.
 * Multiple storylines weave together to create a rich, layered narrative.
 *
 * Storyline priorities follow the A/B/C pattern:
 * - A-storylines (External Spine): Primary external plot defining the overarching objective and physical stakes
 * - B-storylines (Internal/Thematic Spine): Main emotional, relational, or thematic arcs providing underlying meaning
 * - C-storylines (Concurrent Subplot): Independent parallel events, antagonist maneuvers, or background subplots
 *
 * Storylines are distinct from:
 * - Plot Threads: Localized narrative sequences (specific scenarios/episodes) that are the "gears" within storylines
 * - Character Arcs: Individual psychological transformations tracked separately per character
 * - Backstory: Formative events completed before the primary timeline begins
 */
export const StorylineSchema = z.object({
  id: z.string().describe('Unique identifier for this storyline.'),

  name: z.string().describe(
    'Short, evocative name for the storyline (2-4 words). ' +
    'A-storylines: Often named after the main goal ("The Ark Hunt", "The Rebellion"). ' +
    'B-storylines: Often named after relationships ("Indy & Marion", "Luke\'s Training"). ' +
    'C-storylines: Named after subplot focus ("The Ewok Alliance", "Comic Relief Duo").'
  ),

  summary: z.string().optional().describe(
    'A brief description of the storyline (1-3 sentences). ' +
    'Captures how the storyline starts, develops, and resolves. ' +
    'Provides a quick overview without needing to read individual beats.'
  ),

  priority: StorylinePriorityEnum.describe(
    'Storyline priority following the A/B/C pattern. ' +
    'A = The External Spine (primary external plot with physical stakes). ' +
    'B = The Internal/Thematic Spine (emotional, relational, or thematic arcs). ' +
    'C = The Concurrent Subplot (independent parallel events and antagonist maneuvers).'
  ),

  colorIndex: z.number().int().min(0).max(15).describe(
    'Index into the color palette for visual display (0-15).'
  ),

  beats: z.array(StorylineBeatSchema).describe(
    'The sequence of plot events that define this storyline. ' +
    'Should include at minimum Initial State and Final State beats. ' +
    'Additional beats at key structural phases (Inciting Incident, Midpoint, Climax, etc.) ' +
    'flesh out the narrative arc. Multiple beats can share the same structurePhase.'
  ),

  storyQuestions: z.array(StoryQuestionSchema).default([]).describe(
    'Dramatic questions that belong to this storyline. ' +
    'Questions are hooks that keep readers engaged—raised early, answered later. ' +
    'Examples: "Will Indy find the Ark?", "Will Marion forgive Indy?"'
  ),
});

// =============================================================================
// CHAPTER-STORYLINE MAPPING
// =============================================================================

/**
 * Represents a storyline's presence and tension level in a specific chapter.
 * This enables tracking the "handoff" pattern where tension shifts between storylines.
 */
export const ChapterStorylineSchema = z.object({
  storylineId: z.string().describe('Reference to the storyline'),
  tensionLevel: TensionLevelEnum.describe('The tension level for this storyline in this chapter'),
});

// =============================================================================
// STORY PLANNING CONTAINER
// =============================================================================

/**
 * Container for all story planning data.
 * Stored as part of the StoryStructure.
 */
export const StoryPlanningSchema = z.object({
  storylines: z.array(StorylineSchema).describe('A/B/C storylines with their plot, arc, and questions'),
});

// =============================================================================
// TYPE INFERENCE
// =============================================================================

export type TensionLevel = z.infer<typeof TensionLevelEnum>;
export type StorylinePriority = z.infer<typeof StorylinePriorityEnum>;
export type StoryQuestionStatus = z.infer<typeof StoryQuestionStatusEnum>;
export type StoryQuestion = z.infer<typeof StoryQuestionSchema>;
export type Storyline = z.infer<typeof StorylineSchema>;
export type ChapterStoryline = z.infer<typeof ChapterStorylineSchema>;
export type StoryPlanning = z.infer<typeof StoryPlanningSchema>;

// Legacy type aliases for backwards compatibility during migration
/** @deprecated Use Storyline with beats instead */
export type StorylinePlot = { description: string; centralConflict?: string };
/** @deprecated Use Storyline with beats instead */
export type StorylineArc = { centralQuestion: string; openingState?: string; closingState?: string };

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Creates an empty story planning structure.
 */
export function createEmptyStoryPlanning(): StoryPlanning {
  return {
    storylines: [],
  };
}

/**
 * Creates a new storyline with a unique ID and empty beats array.
 */
export function createStoryline(
  name: string,
  priority: StorylinePriority,
  colorIndex: number,
  summary?: string
): Storyline {
  return {
    id: crypto.randomUUID(),
    name,
    ...(summary && { summary }),
    priority,
    colorIndex,
    beats: [],
    storyQuestions: [],
  };
}

/**
 * Creates a new storyline beat with a unique ID.
 *
 * @param globalPhase - Where in the global story timeline this beat occurs (matrix column)
 * @param storylinePhase - What structural function this beat serves for this storyline (beat color)
 * @param plotEvent - The concrete event that occurs
 * @param goalProgress - Progress toward the storyline's goal (-1 to +1)
 * @param options - Optional fields: narrativeState, order
 *
 * For beats where globalPhase and storylinePhase are the same (common for A-storylines),
 * you can pass the same value for both.
 */
export function createStorylineBeat(
  globalPhase: z.infer<typeof StructurePhaseSchema>,
  storylinePhase: z.infer<typeof StructurePhaseSchema>,
  plotEvent: string,
  goalProgress: number,
  options?: { narrativeState?: string; order?: number }
): StorylineBeat {
  return {
    id: crypto.randomUUID(),
    globalPhase,
    storylinePhase,
    plotEvent,
    goalProgress,
    order: options?.order ?? 0,
    ...(options?.narrativeState && { narrativeState: options.narrativeState }),
  };
}

/**
 * Creates a new story question with a unique ID.
 */
export function createStoryQuestion(question: string): StoryQuestion {
  return {
    id: crypto.randomUUID(),
    question,
    status: 'open',
  };
}

/**
 * Creates a chapter-storyline entry with a default tension level.
 */
export function createChapterStoryline(
  storylineId: string,
  tensionLevel: TensionLevel = 'rising'
): ChapterStoryline {
  return {
    storylineId,
    tensionLevel,
  };
}
